import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Linkedin, ExternalLink, Check, SkipForward, Wand2, Loader2, RotateCcw, Copy, MessageSquare, CalendarClock } from "lucide-react";
import { PageBody, PageHeader, EmptyState } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PageTip } from "@/components/FieldHelp";
import { DateTimePicker } from "@/components/DateTimePicker";
import api from "@/lib/api";

const isLoom = (u) => { try { const h = new URL(u).hostname.toLowerCase(); return h === "loom.com" || h === "www.loom.com"; } catch { return false; } };

const STATUS_TABS = [
  { key: "todo", label: "To do" },
  { key: "done", label: "Done" },
  { key: "skipped", label: "Skipped" },
];

export default function LinkedInTasks() {
  const [tab, setTab] = useState("todo");
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy] = useState(null);
  const [tz, setTz] = useState("UTC");

  const load = async () => {
    const { data } = await api.get("/linkedin-tasks", { params: { status: tab } });
    setTasks(data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tab]);
  useEffect(() => { api.get("/org/status").then((r) => setTz(r.data.timezone || "UTC")).catch(() => {}); }, []);

  const setStatus = async (id, status) => {
    setBusy(`s-${id}`);
    try { await api.put(`/linkedin-tasks/${id}`, { status }); toast.success(status === "done" ? "Marked done - activity logged on the contact" : `Marked ${status}`); load(); }
    catch { toast.error("Could not update task"); } finally { setBusy(null); }
  };
  const redraft = async (id) => {
    setBusy(`r-${id}`);
    try { const { data } = await api.post(`/linkedin-tasks/${id}/redraft`); setTasks(tasks.map((t) => t.id === id ? { ...t, suggested_note: data.suggested_note } : t)); toast.success("Note redrafted by Copilot"); }
    catch { toast.error("Redraft failed"); } finally { setBusy(null); }
  };
  const setLoomLocal = (id, url) => setTasks(tasks.map((t) => t.id === id ? { ...t, loom_url: url } : t));
  const saveLoom = async (id, url) => {
    if (url && !isLoom(url)) return toast.error("Loom link must be a loom.com share URL");
    try { await api.put(`/linkedin-tasks/${id}/loom`, { loom_url: url }); toast.success(url ? "Loom link saved" : "Loom link cleared"); }
    catch { toast.error("Could not save Loom link"); }
  };
  const copyLoom = (url) => { navigator.clipboard.writeText(url); toast.success("Loom link copied"); };
  const copyMessage = (t) => { navigator.clipboard.writeText(t.suggested_note || ""); toast.success("Message copied"); };
  const setDueLocal = (id, val) => setTasks(tasks.map((t) => t.id === id ? { ...t, _due_local: val } : t));
  const saveDue = async (id, val) => {
    try { await api.put(`/linkedin-tasks/${id}/due`, { due_at: val || "" }); toast.success(val ? "Due date set" : "Due date cleared"); load(); }
    catch { toast.error("Could not set due date"); }
  };

  const renderCard = (t) => (
    <div key={t.id} className="rounded-xl border border-border/70 bg-card/50 p-5" data-testid={`linkedin-task-${t.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Linkedin className="h-4 w-4 text-sky-500 shrink-0" />
            <span className="font-medium">{t.contact_name}</span>
            <span className="text-xs text-muted-foreground">{t.contact_title}{t.contact_company ? ` · ${t.contact_company}` : ""}</span>
            <Badge variant="outline" className="text-[10px] capitalize">{t.status}</Badge>
            {t.due_at_display && <Badge className={`border-0 text-[10px] ${t.due_bucket === "overdue" ? "bg-rose-500/15 text-rose-500" : t.due_bucket === "today" ? "bg-amber-500/15 text-amber-500" : "bg-muted text-muted-foreground"}`} data-testid={`due-badge-${t.id}`}>{t.due_at_display}</Badge>}
          </div>
          {t.linkedin_url && (
            <a href={t.linkedin_url} target="_blank" rel="noreferrer" className="text-xs text-sky-500 hover:underline inline-flex items-center gap-1 mt-1.5" data-testid={`linkedin-url-${t.id}`}>
              <ExternalLink className="h-3 w-3" /> Open in Navigator
            </a>
          )}
          <div className="mt-3 rounded-lg border border-border/50 bg-accent/30 p-3">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Suggested note (send by hand)</div>
              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => copyMessage(t)} data-testid={`copy-message-${t.id}`}><MessageSquare className="h-3.5 w-3.5 mr-1" /> Copy message</Button>
            </div>
            <p className="text-sm whitespace-pre-wrap">{t.suggested_note}</p>
          </div>
          <div className="mt-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><CalendarClock className="h-3 w-3" /> Due date (in-app reminder only)</div>
            <div className="flex gap-2 items-center flex-wrap">
              <DateTimePicker value={t._due_local ?? ""} tz={tz} onChange={(v) => { setDueLocal(t.id, v); saveDue(t.id, v); }} testId={`linkedin-due-${t.id}`} className="h-9" placeholder="Set a due date" />
              {t.due_at_display && <span className="text-[11px] text-muted-foreground">Saved: {t.due_at_display}</span>}
            </div>
          </div>
          <div className="mt-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">Loom walkthrough (optional)</div>
            <div className="flex gap-2">
              <Input value={t.loom_url || ""} onChange={(e) => setLoomLocal(t.id, e.target.value)} onBlur={() => saveLoom(t.id, t.loom_url || "")}
                placeholder="https://www.loom.com/share/…" data-testid={`linkedin-loom-${t.id}`}
                className={`text-sm ${t.loom_url && !isLoom(t.loom_url) ? "border-rose-500" : ""}`} />
              <Button size="sm" variant="outline" className="rounded-md shrink-0" disabled={!t.loom_url || !isLoom(t.loom_url)} onClick={() => copyLoom(t.loom_url)} data-testid={`copy-loom-${t.id}`}>
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy Loom link
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">15 to 30 seconds. Say their name, one problem, one ask. Record in your Loom account. Same clip is fine for one ICP, not for a whole list of different roles. We do not publish to LinkedIn.</p>
          </div>
        </div>
        <div className="flex sm:flex-col gap-2 shrink-0">
          <Button size="sm" variant="ghost" className="text-amber-500 min-h-[36px]" onClick={() => redraft(t.id)} disabled={busy === `r-${t.id}`} data-testid={`redraft-${t.id}`}>
            {busy === `r-${t.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}<span className="ml-1 text-xs">Redraft</span>
          </Button>
          {t.status !== "done" && (
            <Button size="sm" className="rounded-md bg-amber-500 text-black hover:bg-amber-400 min-h-[36px]" onClick={() => setStatus(t.id, "done")} disabled={busy === `s-${t.id}`} data-testid={`mark-done-${t.id}`}>
              <Check className="h-3.5 w-3.5 mr-1" /> Done
            </Button>
          )}
          {t.status === "todo" && (
            <Button size="sm" variant="outline" className="rounded-md min-h-[36px]" onClick={() => setStatus(t.id, "skipped")} disabled={busy === `s-${t.id}`} data-testid={`skip-${t.id}`}>
              <SkipForward className="h-3.5 w-3.5 mr-1" /> Skip
            </Button>
          )}
          {t.status !== "todo" && (
            <Button size="sm" variant="outline" className="rounded-md min-h-[36px]" onClick={() => setStatus(t.id, "todo")} disabled={busy === `s-${t.id}`} data-testid={`reopen-${t.id}`}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  const BUCKETS = [["overdue", "Overdue"], ["today", "Due today"], ["upcoming", "Upcoming"], ["none", "No due date"]];

  return (
    <PageBody>
      <PageHeader eyebrow="LinkedIn Task Queue" title="Your daily Navigator list"
        subtitle="A manual worklist for the SDR: who to view or message in Sales Navigator by hand. DealsOps never automates LinkedIn or sends DMs for you." />

      <PageTip id="linkedin_tasks">
        These are contacts with no work email (or ones you queued). Open each in your own Navigator license, send the note by hand, then mark it done - that logs activity on the contact. Nothing sends automatically.
      </PageTip>

      <div className="flex gap-1.5 mb-5" data-testid="linkedin-status-tabs">
        {STATUS_TABS.map((s) => (
          <button key={s.key} onClick={() => setTab(s.key)} data-testid={`linkedin-tab-${s.key}`}
            className={`rounded-md px-3 py-1.5 text-sm transition-colors ${tab === s.key ? "bg-amber-500/15 text-amber-500" : "text-muted-foreground hover:text-foreground hover:bg-accent/60"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {tasks.length === 0 ? (
        <EmptyState icon={Linkedin} title="Nothing here yet" body="Import a Sales Navigator CSV (contacts without a work email land here), or add a contact to the queue from the Lead Database." />
      ) : tab === "todo" ? (
        <div className="space-y-6" data-testid="linkedin-task-list">
          {BUCKETS.map(([b, label]) => {
            const list = tasks.filter((t) => (t.due_bucket || "none") === b);
            if (!list.length) return null;
            return (
              <div key={b} data-testid={`linkedin-group-${b}`}>
                <div className="eyebrow mb-2">{label}</div>
                <div className="space-y-3">{list.map(renderCard)}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3" data-testid="linkedin-task-list">{tasks.map(renderCard)}</div>
      )}
    </PageBody>
  );
}
